import React, { forwardRef } from 'react';
import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { Button } from './Button';

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Lucide icon component displayed above the title */
  icon: LucideIcon;
  /** Heading text */
  title: string;
  /** Supporting description text */
  description: string;
  /** Optional primary action */
  action?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState = forwardRef<HTMLDivElement, EmptyStateProps>(
  ({ icon: Icon, title, description, action, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'flex flex-col items-center justify-center text-center',
          'py-12 px-6',
          'bg-[var(--volt-dark-surface)] rounded-[var(--volt-radius-xl)]',
          'border border-[var(--volt-dark-border)]',
          className,
        )}
        {...props}
      >
        <div className="volt-animate-pulse mb-4">
          <Icon
            className="h-16 w-16 text-[var(--volt-text-muted)]"
            aria-hidden="true"
            strokeWidth={1.5}
          />
        </div>

        <h3 className="text-xl font-semibold text-[var(--volt-text-primary)] mb-2">
          {title}
        </h3>

        <p className="text-sm text-[var(--volt-text-secondary)] max-w-md mb-6">
          {description}
        </p>

        {action && (
          <Button variant="primary" onClick={action.onClick}>
            {action.label}
          </Button>
        )}
      </div>
    );
  },
);

EmptyState.displayName = 'EmptyState';
